import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import {
  Button,
  Date,
  Dropdown,
  DropdownMenuItem,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Textarea,
  UtilityInlineGrid,
} from 'rhinostyle';
import moment from 'moment-timezone';
import { DATE_FORMAT_DEFAULT } from '../constants/DateConstants';
import { formatPhone } from '../helpers/PhoneHelpers';

function parseDateAndTime(isoString, timezone) {
  const m = moment.utc(isoString).tz(timezone);
  return {
    date: m.format(DATE_FORMAT_DEFAULT),
    time: m.format('HH:mm'),
  };
}

function buildTimeOptions() {
  const options = [];
  for (let h = 0; h < 24; h += 1) {
    for (let m = 0; m < 60; m += 15) {
      const val = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
      const label = moment(`2000-01-01T${val}`).format('h:mm A');
      options.push({ value: val, label });
    }
  }
  return options;
}

const TIME_OPTIONS = buildTimeOptions();

const ScheduledMessageModal = (props) => {
  const { isOpen, onClose, onConfirm, initialScheduledAt, initialMessageText, phones, selectedPhoneId } = props;

  const userTimezone = moment.tz.guess();
  const tzLabel = `${moment.tz(userTimezone).format('z')} (${userTimezone.replace(/_/g, ' ')})`;

  const today = moment.tz(userTimezone);
  const todayStr = today.format(DATE_FORMAT_DEFAULT);

  const isEditMode = !!initialScheduledAt;
  const initial = isEditMode ? parseDateAndTime(initialScheduledAt, userTimezone) : null;

  const [selectedDate, setSelectedDate] = useState(initial ? initial.date : todayStr);
  const [selectedTime, setSelectedTime] = useState(initial ? initial.time : '');
  const [messageText, setMessageText] = useState(initialMessageText || '');
  const [phoneId, setPhoneId] = useState(selectedPhoneId != null ? Number(selectedPhoneId) : null);
  const [dateError, setDateError] = useState(null);

  useEffect(() => {
    if (phones && phones.length > 0 && phoneId && !phones.some((p) => Number(p.id) === phoneId)) {
      setPhoneId(null);
    }
  }, [phones]);

  const isToday = selectedDate === todayStr;
  const nowTimeStr = today.format('HH:mm');
  const availableTimeOptions = isToday
    ? TIME_OPTIONS.filter((o) => o.value > nowTimeStr)
    : TIME_OPTIONS;

  function handleDateChange(date) {
    const newDate = moment(date).format(DATE_FORMAT_DEFAULT);
    setSelectedDate(newDate);
    setDateError(null);
    if (selectedTime && newDate === todayStr && selectedTime <= nowTimeStr) {
      setSelectedTime('');
    }
  }

  function handleTimeSelect(option) {
    setSelectedTime(option.value);
    setDateError(null);
  }

  function handleConfirm() {
    if (!selectedDate || !selectedTime) return;
    if (isEditMode && !messageText.trim()) return;
    const scheduledMoment = moment.tz(`${selectedDate}T${selectedTime}`, userTimezone);
    if (!scheduledMoment.isAfter(moment())) {
      setDateError('Past date not allowed');
      return;
    }
    onConfirm(scheduledMoment.utc().toISOString(), messageText.trim(), phoneId);
  }

  const isPhoneMissing = phones && phones.length > 0 && !phoneId;
  const isConfirmDisabled = !selectedDate || !selectedTime || (isEditMode && !messageText.trim()) || isPhoneMissing || (phones && phones.length === 0);

  const phoneDropdownLabel = () => {
    if (!phones || phones.length === 0) return 'No phone available';
    if (phoneId) return formatPhone(phones.find((p) => Number(p.id) === phoneId)?.value || '') || 'Select phone';
    return 'Select phone';
  };

  const selectedTimeLabel = selectedTime
    ? TIME_OPTIONS.find((o) => o.value === selectedTime)?.label || selectedTime
    : 'Time';

  return (
    <Modal open={isOpen}>
      <ModalHeader title={isEditMode ? 'Edit message' : 'Schedule message'} onClose={onClose} />
      <ModalBody>
        {isEditMode && (
          <Textarea
            name="messageText"
            label="Message"
            initialValue={messageText}
            onChange={(name, value) => setMessageText(value)}
          />
        )}
        {phones && (
          <div className="u-m-b">
            <p className="u-text-small u-text-muted u-m-b-small">To Phone</p>
            <Dropdown
              label={phoneDropdownLabel()}
              type="outline"
              block
              disabled={phones.length === 0}
            >
              {phones.map((phone) => (
                <DropdownMenuItem
                  key={phone.id}
                  id={String(phone.id)}
                  label={formatPhone(phone.value || '') || phone.value}
                  onClick={() => setPhoneId(Number(phone.id))}
                  active={Number(phone.id) === phoneId}
                />
              ))}
            </Dropdown>
          </div>
        )}
        <p className="u-text-muted u-text-small u-m-b u-m-t">Time zone: {tzLabel}</p>
        <div className="row">
          <div className="column-8@small">
            <Date
              name="scheduledDate"
              selected={selectedDate ? moment(selectedDate, DATE_FORMAT_DEFAULT) : null}
              onChange={handleDateChange}
              minDate={today}
              explanationMessage=""
            />
          </div>
          <div className="column-4@small">
            <Dropdown
              label={selectedTimeLabel}
              type="outline"
              block
              lockLabel
            >
              {availableTimeOptions.map((opt) => (
                <DropdownMenuItem
                  key={opt.value}
                  id={opt.value}
                  label={opt.label}
                  onClick={() => handleTimeSelect(opt)}
                  active={opt.value === selectedTime}
                />
              ))}
            </Dropdown>
          </div>
        </div>
        {dateError && <p className="u-text-danger u-text-small u-m-t-small">{dateError}</p>}
      </ModalBody>
      <ModalFooter>
        <UtilityInlineGrid align="between">
          <Button type="link-muted" className="u-p-l-0" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={handleConfirm}
            disabled={isConfirmDisabled}
          >
            {isEditMode ? 'Update Schedule' : 'Schedule a Message'}
          </Button>
        </UtilityInlineGrid>
      </ModalFooter>
    </Modal>
  );
};

ScheduledMessageModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  initialScheduledAt: PropTypes.string,
  initialMessageText: PropTypes.string,
  phones: PropTypes.array,
  selectedPhoneId: PropTypes.number,
};

export default ScheduledMessageModal;
